import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  RestartAlt as RestartAltIcon,
  PowerSettingsNew as ShutdownIcon,
  Delete as DeleteIcon,
  Terminal as TerminalIcon,
  Close as CloseIcon,
  Update as UpdateIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../context/authContext';
import useFleet from '../../hooks/useFleet';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const SERVICE_LABELS = {
  locationserver: 'Location Server',
  briefingplayer: 'Briefing Player',
  audioplayer: 'Audio Player',
  printserver: 'Printserver',
};

const SERVICE_ORDER = ['locationserver', 'briefingplayer', 'audioplayer', 'printserver'];

const CONNECTION_STATUS_CONFIG = {
  online: { label: 'Online', color: 'success' },
  stale: { label: 'Verzögert', color: 'warning' },
  offline: { label: 'Offline', color: 'error' },
  unknown: { label: 'Unbekannt', color: 'default' },
};

const formatBytes = bytes => {
  if (bytes == null) return '–';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

const formatMemory = (usedBytes, totalBytes) => {
  if (usedBytes == null) return '–';
  const used = formatBytes(usedBytes);
  if (totalBytes == null) return used;
  const pct = Math.round((usedBytes / totalBytes) * 100);
  const total = formatBytes(totalBytes);
  return `${used} / ${total} (${pct}%)`;
};

const formatUptime = ms => {
  if (ms == null) return '–';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatTs = ts => {
  if (!ts) return '–';
  return new Date(ts).toLocaleString('de-DE');
};

function ConnectionChip({ status }) {
  const cfg = CONNECTION_STATUS_CONFIG[status] ?? CONNECTION_STATUS_CONFIG.unknown;
  return <Chip label={cfg.label} color={cfg.color} size='small' />;
}

function AppStatusSection({ service, appStatus }) {
  if (!appStatus) return null;

  // locationserver: show httpConnection + cache stats
  if (service === 'locationserver') {
    const { httpConnection, cache } = appStatus;
    const stats = cache?.stats ?? {};
    const lastFetchTimes = cache?.lastFetchTimes ?? {};
    return (
      <Box sx={{ mt: 0.5 }}>
        <Stack direction='row' flexWrap='wrap' gap={0.5} alignItems='center'>
          {httpConnection && (
            <Chip
              label={httpConnection.online ? 'Backend: Online' : 'Backend: Offline'}
              color={httpConnection.online ? 'success' : 'error'}
              size='small'
            />
          )}
        </Stack>
        <Grid container spacing={1} mt={0.5}>
          {Object.entries(stats).map(([type, s]) => (
            <Grid item xs={12} key={type}>
              <Typography variant='caption' color='text.secondary'>
                {type}: {s?.total ?? '–'} Einträge
                {lastFetchTimes[type] &&
                  ` · Fetch: ${new Date(lastFetchTimes[type]).toLocaleTimeString('de-DE')}`}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // briefingplayer: show player state
  if (service === 'briefingplayer') {
    const state = appStatus.status ?? appStatus.state ?? JSON.stringify(appStatus);
    return <Chip label={`Player: ${state}`} size='small' variant='outlined' sx={{ mt: 0.5 }} />;
  }

  // audioplayer: show playback info
  if (service === 'audioplayer') {
    const state = appStatus.status ?? appStatus.state ?? appStatus.playing ?? null;
    if (!state) return null;
    return (
      <Chip label={`Audio: ${String(state)}`} size='small' variant='outlined' sx={{ mt: 0.5 }} />
    );
  }

  // printserver: show printer status
  if (service === 'printserver') {
    const state = appStatus.status ?? appStatus.printerStatus ?? null;
    if (!state) return null;
    return (
      <Chip label={`Drucker: ${String(state)}`} size='small' variant='outlined' sx={{ mt: 0.5 }} />
    );
  }

  return null;
}

function ServiceCard({
  invoiceCode,
  service,
  statusData,
  connectionStatus,
  canRestart,
  canShutdown,
  canAdvanced,
  canRead,
  canDelete,
  loadingAction,
  onCommand,
  onOpenLogs,
  onFetchErrors,
}) {
  const pm2 = statusData?.pm2 ?? {};
  const git = statusData?.git ?? {};
  const system = statusData?.system ?? {};
  const errors = statusData?.errors ?? [];
  const appStatus = statusData?.appStatus ?? null;
  const isDisabled = connectionStatus === 'unknown' || !!loadingAction;

  const [extendedErrors, setExtendedErrors] = useState(null);
  const [fetchingErrors, setFetchingErrors] = useState(false);

  const handleFetchMoreErrors = async () => {
    setFetchingErrors(true);
    try {
      const result = await onFetchErrors(invoiceCode, service);
      setExtendedErrors(result?.errorLines ?? []);
    } catch (err) {
      // On error keep original status errors visible; just show toast upstream
      console.error('[FleetOverview] log:errors failed:', err.message);
    } finally {
      setFetchingErrors(false);
    }
  };

  const label = SERVICE_LABELS[service] ?? service;
  const isUnknown = connectionStatus === 'unknown' && service !== 'locationserver';

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {/* Header */}
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant='subtitle2' fontWeight='bold'>
          {label}
        </Typography>
        <Stack direction='row' gap={0.5} alignItems='center'>
          {git.updateAvailable && (
            <Tooltip title='Update verfügbar'>
              <Chip icon={<UpdateIcon />} label='Update' color='warning' size='small' />
            </Tooltip>
          )}
          <ConnectionChip status={connectionStatus} />
        </Stack>
      </Stack>

      {isUnknown && connectionStatus === 'unknown' && !statusData && (
        <Typography variant='body2' color='text.secondary'>
          Kein Status bekannt. Gerät noch nie online oder locationserver offline.
        </Typography>
      )}

      {statusData && (
        <>
          {/* Git info */}
          <Stack direction='row' flexWrap='wrap' gap={0.5}>
            {git.branch && <Chip label={git.branch} size='small' variant='outlined' />}
            {git.commit && (
              <Tooltip title={git.commitDate ? `Commit: ${formatTs(git.commitDate)}` : ''}>
                <Chip label={git.commit} size='small' variant='outlined' />
              </Tooltip>
            )}
            {pm2.env && <Chip label={pm2.env} size='small' variant='outlined' color='info' />}
          </Stack>
          {git.lastPull && (
            <Typography variant='caption' color='text.secondary'>
              Letzter Pull: {formatTs(git.lastPull)}
            </Typography>
          )}

          {/* PM2 stats */}
          <Grid container spacing={1}>
            {[
              { label: 'Status', value: pm2.status ?? '–' },
              { label: 'CPU (App)', value: pm2.cpu != null ? `${pm2.cpu}%` : '–' },
              { label: 'RAM (App)', value: formatMemory(pm2.memory, system.memTotalBytes), xs: 8 },
              { label: 'Uptime', value: formatUptime(pm2.uptime) },
              { label: 'Restarts', value: pm2.restarts ?? '–' },
            ].map(({ label, value, xs = 4 }) => (
              <Grid item xs={xs} key={label}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  {label}
                </Typography>
                <Typography variant='body2'>{value}</Typography>
              </Grid>
            ))}
          </Grid>

          {/* System info */}
          <Stack direction='row' flexWrap='wrap' gap={0.5} mt={0.5}>
            {system.hostname && (
              <Typography variant='caption' color='text.secondary'>
                {system.hostname}
              </Typography>
            )}
            {system.ip && (
              <Typography variant='caption' color='text.secondary'>
                · {system.ip}
              </Typography>
            )}
            {system.diskFreeBytes != null && system.diskTotalBytes != null && (
              <Typography variant='caption' color='text.secondary'>
                · Disk: {formatBytes(system.diskFreeBytes)} frei /{' '}
                {formatBytes(system.diskTotalBytes)}
              </Typography>
            )}
            {system.networkLatencyMs != null && (
              <Typography variant='caption' color='text.secondary'>
                · Latenz: {system.networkLatencyMs} ms
              </Typography>
            )}
          </Stack>

          {/* App status (from existing service status topics) — collapsible */}
          {appStatus && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', mt: 0.5 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 32, py: 0, px: 1 }}
              >
                <Typography variant='caption' color='text.secondary'>
                  Gerätestatus
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                <AppStatusSection service={service} appStatus={appStatus} />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Last errors */}
          {errors.length > 0 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'warning.main', mt: 0.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, py: 0 }}>
                <Typography variant='caption' color='warning.main'>
                  Letzte Fehler ({errors.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                {extendedErrors !== null ? (
                  <>
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                      mb={0.5}
                    >
                      <Typography variant='caption' color='text.secondary'>
                        Log-Datei ({extendedErrors.length} Zeilen)
                      </Typography>
                      <Button
                        size='small'
                        variant='text'
                        sx={{ fontSize: 10, p: 0, minWidth: 0 }}
                        onClick={() => setExtendedErrors(null)}
                      >
                        ← Zurück
                      </Button>
                    </Stack>
                    {extendedErrors.length === 0 ? (
                      <Typography variant='caption' color='text.secondary'>
                        Keine Fehler in der Log-Datei.
                      </Typography>
                    ) : (
                      extendedErrors.map((line, i) => (
                        <Typography
                          key={i}
                          variant='caption'
                          display='block'
                          sx={{ wordBreak: 'break-all', mb: 0.5 }}
                        >
                          {line}
                        </Typography>
                      ))
                    )}
                  </>
                ) : (
                  <>
                    {errors.map((e, i) => (
                      <Typography
                        key={i}
                        variant='caption'
                        display='block'
                        sx={{ wordBreak: 'break-all', mb: 0.5 }}
                      >
                        <span style={{ opacity: 0.6 }}>{formatTs(e.ts)}</span> {e.message}
                      </Typography>
                    ))}
                    <Button
                      size='small'
                      variant='text'
                      sx={{ mt: 0.5, p: 0, minWidth: 0, fontSize: 11 }}
                      onClick={handleFetchMoreErrors}
                      disabled={fetchingErrors || connectionStatus === 'unknown'}
                      startIcon={fetchingErrors ? <CircularProgress size={10} /> : null}
                    >
                      Mehr laden (100 Zeilen)
                    </Button>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}

      {/* Actions */}
      <Box sx={{ mt: 'auto', pt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/* App management row */}
        {(canRestart || canAdvanced) && (
          <Stack direction='row' flexWrap='wrap' gap={0.5}>
            {canRestart && (
              <Tooltip title='PM2 Neustart'>
                <span>
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={
                      loadingAction === 'pm2:restart' ? (
                        <CircularProgress size={14} />
                      ) : (
                        <RestartAltIcon />
                      )
                    }
                    onClick={() => onCommand(invoiceCode, service, 'pm2:restart')}
                    disabled={isDisabled}
                  >
                    Restart
                  </Button>
                </span>
              </Tooltip>
            )}
            {canAdvanced && (
              <>
                <Tooltip title='git pull'>
                  <span>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={
                        loadingAction === 'git:pull' ? <CircularProgress size={14} /> : null
                      }
                      onClick={() => onCommand(invoiceCode, service, 'git:pull')}
                      disabled={isDisabled}
                    >
                      git pull
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title='npm install'>
                  <span>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={
                        loadingAction === 'npm:install' ? <CircularProgress size={14} /> : null
                      }
                      onClick={() => onCommand(invoiceCode, service, 'npm:install')}
                      disabled={isDisabled}
                    >
                      npm install
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title='PM2 Clean-Restart (stop → delete → start)'>
                  <span>
                    <Button
                      size='small'
                      variant='outlined'
                      color='warning'
                      startIcon={
                        loadingAction === 'pm2:clean-restart' ? (
                          <CircularProgress size={14} />
                        ) : null
                      }
                      onClick={() => onCommand(invoiceCode, service, 'pm2:clean-restart')}
                      disabled={isDisabled}
                    >
                      Clean Restart
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Stack>
        )}
        {/* System ops + utility icons row */}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' flexWrap='wrap' gap={0.5}>
            {canRestart && (
              <Tooltip title='System Neustart (sudo reboot)'>
                <span>
                  <Button
                    size='small'
                    variant='outlined'
                    color='warning'
                    startIcon={
                      loadingAction === 'system:restart' ? (
                        <CircularProgress size={14} />
                      ) : (
                        <RestartAltIcon />
                      )
                    }
                    onClick={() => onCommand(invoiceCode, service, 'system:restart', {}, true)}
                    disabled={isDisabled}
                  >
                    Sys. Restart
                  </Button>
                </span>
              </Tooltip>
            )}
            {canShutdown && (
              <Tooltip title='System herunterfahren'>
                <span>
                  <Button
                    size='small'
                    variant='outlined'
                    color='error'
                    startIcon={
                      loadingAction === 'system:shutdown' ? (
                        <CircularProgress size={14} />
                      ) : (
                        <ShutdownIcon />
                      )
                    }
                    onClick={() => onCommand(invoiceCode, service, 'system:shutdown', {}, true)}
                    disabled={isDisabled}
                  >
                    Shutdown
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
          <Stack direction='row' gap={0.5}>
            {canRead && (
              <Tooltip title='Logs streamen'>
                <IconButton
                  size='small'
                  onClick={() => onOpenLogs(invoiceCode, service)}
                  disabled={connectionStatus === 'unknown'}
                >
                  <TerminalIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title='Aus Fleet entfernen'>
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => onCommand(invoiceCode, service, '_delete')}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

function LogDrawer({ open, onClose, invoiceCode, service, logLines, onStart, onStop }) {
  const theme = useTheme();
  const logTextAreaSx = useMemo(() => {
    const bg =
      theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900];
    return {
      flex: 1,
      overflowY: 'auto',
      background: bg,
      borderRadius: 1,
      p: 1,
      fontFamily: 'monospace',
      fontSize: 12,
      color: theme.palette.getContrastText(bg),
      border: `1px solid ${theme.palette.divider}`,
    };
  }, [theme]);
  const bottomRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const key = invoiceCode && service ? `${invoiceCode}:${service}` : null;
  const lines = key ? (logLines[key] ?? []) : [];

  useEffect(() => {
    if (open) {
      setStreaming(false);
    }
  }, [open, invoiceCode, service]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lines.length]);

  const handleStart = async () => {
    setStreaming(true);
    await onStart(invoiceCode, service);
  };

  const handleStop = async () => {
    setStreaming(false);
    await onStop(invoiceCode, service);
  };

  return (
    <Drawer anchor='right' open={open} onClose={onClose} PaperProps={{ sx: { width: 600, p: 2 } }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
        <Typography variant='h6'>
          Logs — {SERVICE_LABELS[service] ?? service} ({invoiceCode})
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Stack direction='row' gap={1} mb={1}>
        <Button
          size='small'
          variant='contained'
          startIcon={<PlayArrowIcon />}
          onClick={handleStart}
          disabled={streaming}
        >
          Starten
        </Button>
        <Button
          size='small'
          variant='outlined'
          startIcon={<StopIcon />}
          onClick={handleStop}
          disabled={!streaming}
        >
          Stoppen
        </Button>
        <Typography variant='caption' color='text.secondary' alignSelf='center'>
          {lines.length} Zeilen
        </Typography>
      </Stack>
      <Box sx={logTextAreaSx}>
        {lines.length === 0 && (
          <Typography variant='caption' color='text.secondary'>
            Noch keine Log-Zeilen. Streaming starten.
          </Typography>
        )}
        {lines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
        <div ref={bottomRef} />
      </Box>
    </Drawer>
  );
}

function FleetOverview() {
  const { checkRight, checkRightAny } = useAuth();
  const canRead = checkRight?.('fleet:read') || checkRight?.('fleet:read:own');
  const canRestart = checkRightAny?.('fleet:action:restart');
  const canShutdown = checkRightAny?.('fleet:action:shutdown');
  const canAdvanced = checkRightAny?.('fleet:action:advanced');
  const canDelete = checkRight?.('fleet:read'); // full read required for decommission

  const {
    fleetStatuses,
    isLoading,
    fetchAllStatuses,
    sendCommand,
    startLogStream,
    stopLogStream,
    removeDevice,
    logLines,
  } = useFleet();

  // commandLoading: `${invoiceCode}:${service}` → action string (or undefined when idle)
  const [commandLoading, setCommandLoading] = useState({});
  const [expandedLocations, setExpandedLocations] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, label: '' });
  const [logDrawer, setLogDrawer] = useState({ open: false, invoiceCode: null, service: null });

  useEffect(() => {
    fetchAllStatuses();
  }, [fetchAllStatuses]);

  const setServiceLoading = (invoiceCode, service, action) =>
    setCommandLoading(prev => ({ ...prev, [`${invoiceCode}:${service}`]: action }));

  const clearServiceLoading = (invoiceCode, service) =>
    setCommandLoading(prev => {
      const next = { ...prev };
      delete next[`${invoiceCode}:${service}`];
      return next;
    });

  const handleCommand = useCallback(
    async (invoiceCode, service, action, params = {}, needsConfirm = false) => {
      if (action === '_delete') {
        setConfirmDialog({
          open: true,
          label: `${SERVICE_LABELS[service] ?? service} (${invoiceCode}) aus Fleet entfernen?`,
          onConfirm: async () => {
            setConfirmDialog({ open: false });
            try {
              await removeDevice(invoiceCode, service);
              toast.success('Gerät aus Fleet entfernt.');
            } catch (err) {
              toast.error(`Fehler: ${err.message}`);
            }
          },
        });
        return;
      }

      if (needsConfirm) {
        setConfirmDialog({
          open: true,
          action,
          label: `Aktion "${action}" auf ${SERVICE_LABELS[service] ?? service} (${invoiceCode}) ausführen?`,
          onConfirm: () => {
            setConfirmDialog({ open: false });
            executeCommand(invoiceCode, service, action, params);
          },
        });
        return;
      }

      executeCommand(invoiceCode, service, action, params);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [removeDevice]
  );

  const executeCommand = async (invoiceCode, service, action, params) => {
    setServiceLoading(invoiceCode, service, action);
    try {
      const result = await sendCommand(invoiceCode, service, action, params);
      toast.success(result?.message || `Aktion "${action}" erfolgreich.`);
      if (['git:pull', 'git:checkout', 'npm:install', 'pm2:clean-restart'].includes(action)) {
        setTimeout(fetchAllStatuses, 3000);
      }
    } catch (err) {
      toast.error(`Fehler bei "${action}": ${err.message}`);
    } finally {
      clearServiceLoading(invoiceCode, service);
    }
  };

  const handleOpenLogs = (invoiceCode, service) => {
    setLogDrawer({ open: true, invoiceCode, service });
  };

  if (!canRead) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
        <Alert severity='error'>Keine Berechtigung zum Anzeigen des Fleet-Status.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography variant='h5'>Fleet-Übersicht</Typography>
        <Stack direction='row' gap={1} alignItems='center'>
          <Tooltip title={expandedLocations.size > 0 ? 'Alle einklappen' : 'Alle ausklappen'}>
            <IconButton
              onClick={() =>
                setExpandedLocations(
                  expandedLocations.size > 0
                    ? new Set()
                    : new Set(fleetStatuses.map(l => l.invoiceCode))
                )
              }
              disabled={fleetStatuses.length === 0}
            >
              {expandedLocations.size > 0 ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title='Aktualisieren'>
            <IconButton onClick={fetchAllStatuses} disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {fleetStatuses.length === 0 && !isLoading && (
        <Alert severity='info'>Keine Fleet-Geräte gefunden.</Alert>
      )}

      {[...fleetStatuses].sort((a, b) => a.invoiceCode.localeCompare(b.invoiceCode)).map(location => {
        const onlineCount = Object.values(location.services ?? {}).filter(
          s => s.connectionStatus === 'online'
        ).length;
        const totalCount = Object.keys(location.services ?? {}).length;

        return (
          <Accordion
            key={location.invoiceCode}
            expanded={expandedLocations.has(location.invoiceCode)}
            onChange={(_, isExpanded) =>
              setExpandedLocations(prev => {
                const next = new Set(prev);
                isExpanded ? next.add(location.invoiceCode) : next.delete(location.invoiceCode);
                return next;
              })
            }
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction='row' gap={1} alignItems='center' width='100%'>
                <Typography fontWeight='bold'>{location.invoiceCode}</Typography>
                <Chip
                  label={`${onlineCount}/${totalCount} online`}
                  size='small'
                  color={
                    onlineCount === totalCount ? 'success' : onlineCount > 0 ? 'warning' : 'error'
                  }
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {SERVICE_ORDER.map(service => {
                  const svc = location.services?.[service];
                  if (!svc && service !== 'locationserver') return null;

                  const connectionStatus = svc?.connectionStatus ?? 'unknown';
                  const loadingAction =
                    commandLoading[`${location.invoiceCode}:${service}`] ?? null;

                  return (
                    <Grid item xs={12} sm={6} key={service}>
                      <ServiceCard
                        invoiceCode={location.invoiceCode}
                        service={service}
                        statusData={svc?.status ?? null}
                        connectionStatus={connectionStatus}
                        canRestart={canRestart}
                        canShutdown={canShutdown}
                        canAdvanced={canAdvanced}
                        canRead={canRead}
                        canDelete={canDelete}
                        loadingAction={loadingAction}
                        onCommand={handleCommand}
                        onOpenLogs={handleOpenLogs}
                        onFetchErrors={(inv, svc) =>
                          sendCommand(inv, svc, 'log:errors', { lines: 100 })
                        }
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Confirmation dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false })}>
        <DialogTitle>Bestätigung</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.label}</DialogContentText>
          {confirmDialog.action === 'system:shutdown' && (
            <Alert severity='error' sx={{ mt: 2 }}>
              <strong>Achtung:</strong> Nach dem Herunterfahren kann das Gerät <strong>nicht remote neu gestartet</strong> werden.
              Ein Neustart ist nur <strong>vor Ort</strong> möglich.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false })}>Abbrechen</Button>
          <Button onClick={confirmDialog.onConfirm} variant='contained' color='error' autoFocus>
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Log drawer */}
      <LogDrawer
        open={logDrawer.open}
        onClose={() => setLogDrawer({ open: false, invoiceCode: null, service: null })}
        invoiceCode={logDrawer.invoiceCode}
        service={logDrawer.service}
        logLines={logLines}
        onStart={startLogStream}
        onStop={stopLogStream}
      />
    </Box>
  );
}

export default FleetOverview;
